import React, { useState, useEffect } from 'react';
import { FontIcon } from '@fluentui/react/lib/Icon';
import { IconButton } from '@fluentui/react/lib/Button';
import { mergeStyles } from '@fluentui/react/lib/Styling';
import { userAPI } from '../UserAPI';
import '../App.sass';

export interface Suggestion {
    id: string;
    icon: string;
    iconStyles: string;
    examples: string[];
    index: number;
    screenShotURL: string;
}

interface SuggestionsProps {
    onSuggestionClick: (suggestion: Suggestion) => void;
    onClose?: () => void;
    showControls?: boolean;
    refreshIntervalSeconds?: number;
}

const iconClass = {
    fontSize: 30,
    height: 30,
    width: 30,
};

const suggestionIconStyles = [
    mergeStyles({ color: 'darkblue', ...iconClass }),
    mergeStyles({ color: 'darkred', ...iconClass }),
    mergeStyles({ color: 'darkgreen', ...iconClass }),
    mergeStyles({ color: 'purple', ...iconClass })
];

export const Suggestions: React.FC<SuggestionsProps> = ({
    onSuggestionClick,
    onClose = () => { },
    showControls = true,
    refreshIntervalSeconds = 10
}) => {
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [skills, setSkills] = useState([]);
    const [resetSuggestionsTimer, setResetSuggestionsTimer] = useState(true);

    const selectSuggestions = (skills: any) => {
        const shuffledSkills = skills.sort(() => 0.5 - Math.random());
        const shuffledIconStyles = suggestionIconStyles.sort(() => 0.5 - Math.random());

        const newSuggestions = shuffledSkills.slice(0, 4).map((skill: any, index: number) => {
            return {
                ...skill,
                iconStyles: shuffledIconStyles[index],
                index: Date.now() % skill.examples.length,
            };
        });

        setSuggestions(newSuggestions);
    };

    const handleRefresh = () => {
        if (skills) {
            selectSuggestions(skills);
        }
        setResetSuggestionsTimer(true);
    };

    // Fetch skills on component mount
    useEffect(() => {
        const fetchSkills = async () => {
            try {
                const newSkills = await userAPI.getSkills();
                setSkills(newSkills);
                selectSuggestions(newSkills);
            } catch (error) {
                console.error('Error fetching skills:', error);
            }
        };

        fetchSkills();
    }, []);

    // Auto-refresh suggestions based on configured interval
    useEffect(() => {
        if (!resetSuggestionsTimer) {
            return;
        }

        let interval: NodeJS.Timeout;

        if (skills.length > 0) {
            interval = setInterval(() => {
                selectSuggestions(skills);
            }, refreshIntervalSeconds * 1000);
        }

        return () => clearInterval(interval);
    }, [skills, resetSuggestionsTimer, refreshIntervalSeconds]);

    return (
        <div>
            {showControls && (
                <div className="paddingM displayFlex justifyContentEnd gapsM">
                    <IconButton
                        iconProps={{ iconName: 'Refresh' }}
                        title="Refresh"
                        aria-label="Refresh"
                        onClick={handleRefresh}
                    />
                    <IconButton
                        iconProps={{ iconName: 'Cancel' }}
                        title="Close"
                        aria-label="Close"
                        onClick={onClose}
                    />
                </div>
            )}
            <div className="suggestions">
                {suggestions.slice(0, 3).map((suggestion, index) => (
                    <div
                        key={`${suggestion.id}-${index}`}
                        className='suggestion rollOut'
                        onClick={() => onSuggestionClick(suggestion)}
                    >
                        <FontIcon
                            iconName={suggestion.icon}
                            className={suggestion.iconStyles}
                            aria-label="send"
                        />
                        <div className='request'>
                            {suggestion.examples[suggestion.index]}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}; 